import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// The prototype's badge labels are --text-muted. They previously used a slightly darker grey,
// which read as a second muted tone beside everything else.
const VARIANTS = {
    soft: {
        background: "var(--pw-neutral-100)",
        color: "var(--text-muted)",
        fontSize: "var(--pw-text-xs)",
        radius: "var(--pw-radius-pill)",
        textTransform: "none",
        letterSpacing: "normal",
        border: "none",
    },
    count: {
        background: "var(--pw-neutral-75)",
        color: "var(--text-muted)",
        fontSize: "var(--pw-text-sm)",
        radius: "var(--pw-radius-pill)",
        textTransform: "none",
        letterSpacing: "normal",
        border: "1px solid var(--pw-neutral-200)",
    },
    account: {
        background: "var(--pw-neutral-100)",
        color: "var(--text-muted)",
        fontSize: "10px",
        radius: "var(--pw-radius-pill)",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        border: "none",
    },
    tag: {
        background: "var(--pw-neutral-100)",
        color: "var(--pw-neutral-800)",
        fontSize: "11.5px",
        radius: "var(--pw-radius-sm)",
        textTransform: "none",
        letterSpacing: "0.03em",
        border: "none",
    },
    primary: {
        background: "var(--pw-primary)",
        color: "var(--text-on-brand)",
        fontSize: "var(--pw-text-xs)",
        radius: "var(--pw-radius-pill)",
        textTransform: "none",
        letterSpacing: "normal",
        border: "none",
    },
    warning: {
        background: "var(--pw-warning-note-bg)",
        color: "var(--pw-warning-fg)",
        fontSize: "var(--pw-text-xs)",
        radius: "var(--pw-radius-pill)",
        textTransform: "none",
        letterSpacing: "normal",
        border: "1px solid var(--pw-warning-border)",
    },
    // Reuses the warning tokens verbatim so a future warning restyle can't silently recolor this badge too.
    vip: {
        background: "var(--pw-warning-note-bg)",
        color: "var(--pw-warning-fg)",
        fontSize: "var(--pw-text-xs)",
        radius: "var(--pw-radius-pill)",
        textTransform: "none",
        letterSpacing: "normal",
        border: "1px solid var(--pw-warning-border)",
    },
    success: {
        background: "var(--pw-success-bg)",
        color: "var(--pw-success-fg)",
        fontSize: "var(--pw-text-xs)",
        radius: "var(--pw-radius-pill)",
        textTransform: "none",
        letterSpacing: "normal",
        border: "1px solid var(--pw-success-border)",
    },
    danger: {
        background: "var(--pw-danger-bg)",
        color: "var(--pw-danger-fg)",
        fontSize: "var(--pw-text-xs)",
        radius: "var(--pw-radius-pill)",
        textTransform: "none",
        letterSpacing: "normal",
        border: "1px solid var(--pw-danger-border)",
    },
    info: {
        background: "var(--pw-info-tile-bg)",
        color: "var(--pw-primary)",
        fontSize: "var(--pw-text-xs)",
        radius: "var(--pw-radius-pill)",
        textTransform: "none",
        letterSpacing: "normal",
        border: "1px solid var(--pw-info-border)",
    },
};

// sm tightens padding/font-size for compact contexts (e.g. rail badges); md (the default) has no
// entry here, so it falls through to the pre-existing hardcoded values and stays byte-identical.
const SIZE_OVERRIDES = {
    sm: { padding: "1px 7px", fontSize: "10px" },
};

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    /* Grid and flex parents stretch their children; a badge must hug its label. */
    justify-self: start;
    align-self: center;
    /* Flex parents (e.g. the nav-rail org switcher) must not squeeze a badge when space is tight. */
    flex-shrink: 0;
    width: fit-content;
    gap: var(--gap-s);
    padding: ${(props) => SIZE_OVERRIDES[props.$size]?.padding || "3px 9px"};
    /* The prototype's count badge is semibold, not bold — measured against the running app,
       where this was the one value that did not match. */
    font-weight: var(--pw-weight-semibold);
    line-height: var(--pw-leading-snug);
    white-space: nowrap;
    background: ${(props) => VARIANTS[props.$variant].background};
    color: ${(props) => VARIANTS[props.$variant].color};
    font-size: ${(props) => SIZE_OVERRIDES[props.$size]?.fontSize || VARIANTS[props.$variant].fontSize};
    border-radius: ${(props) => VARIANTS[props.$variant].radius};
    text-transform: ${(props) => VARIANTS[props.$variant].textTransform};
    letter-spacing: ${(props) => VARIANTS[props.$variant].letterSpacing};
    border: ${(props) => VARIANTS[props.$variant].border};
`;

export const PathwayBadge = ({ children, variant = "soft", size = "md", dataCy }) => (
    <Badge $variant={VARIANTS[variant] ? variant : "soft"} $size={size} data-cy={dataCy}>
        {children}
    </Badge>
);

PathwayBadge.propTypes = {
    children: PropTypes.node.isRequired,
    variant: PropTypes.oneOf(["soft", "count", "account", "tag", "primary", "warning", "success", "danger", "info", "vip"]),
    size: PropTypes.oneOf(["sm", "md"]),
    dataCy: PropTypes.string,
};

export default PathwayBadge;
